/**
 * Review & Deploy ("Review + create"): the grouped, fully resolved Azure Virtual Machine
 * settings, the plan-first notice (who can approve), the not-installed notice and the cost.
 *
 * The review is built from the payload only — a password never appears here.
 */
import clsx from 'clsx'
import { AlertTriangle, ShieldCheck } from 'lucide-react'
import styles from '@/pages/NewDeploymentPage.module.css'
import { useAzureSubscription, useSelectedSize } from '@/hooks/useAppriseAzure'
import { useAppriseVmOptions } from '@/hooks/useProducts'
import { regionToLocation } from './azureNames'
import { applyIsDisabled } from '@/components/plan-approval/planApproval'
import { formatCurrency } from '@/utils/format'
import { APPRISE_UNVERIFIED_NOTICE } from '@/utils/apprise'
import type { AppriseVmConfig, AppriseVmOptions } from '@/types'
import { appriseReviewGroups, approverSentence, type AppriseReviewContext, type AppriseReviewRow } from './appriseReview'

function Row({ label, value, mono }: AppriseReviewRow) {
  return (
    <div className={styles.reviewRow}>
      <span className={styles.reviewLabel}>{label}</span>
      <span className={clsx(styles.reviewValue, mono && styles.reviewValueMono)} style={{ whiteSpace: 'pre-line' }}>{value}</span>
    </div>
  )
}

/** Every resolved setting, grouped. Also used by the deployment detail "resolved configuration". */
export function AppriseReviewGroups({ config, ...ctx }: { config: AppriseVmConfig } & AppriseReviewContext) {
  return (
    <>
      {appriseReviewGroups(config, ctx).map((g, idx) => (
        <div key={g.title} style={{ marginTop: idx === 0 ? 0 : 16 }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--aptean-text-muted)', marginBottom: 8 }}>
            {g.title}
          </div>
          <div className={styles.reviewTable}>
            {g.rows.map((r) => <Row key={r.label} {...r} />)}
          </div>
        </div>
      ))}
    </>
  )
}

export function AppriseReviewSection({ config, region }: { config: AppriseVmConfig; region?: string }) {
  const { data: options } = useAppriseVmOptions(true)
  const { data: subscription } = useAzureSubscription(true)
  const location = regionToLocation(region ?? '')
  const { size } = useSelectedSize(location, config.server.vm_size, config.server.deployment_mode === 'create_new')
  const reviewOptions: Pick<AppriseVmOptions, 'images'> | undefined = options
  return (
    <div className={styles.reviewSection} style={{ marginTop: 20 }}>
      <h3 className={styles.reviewSectionTitle}>Azure Virtual Machine (Apprise)</h3>
      <div className={styles.lbInfoBanner} role="note"
        style={{ marginTop: 0, marginBottom: 12, borderColor: 'rgba(245, 166, 35, 0.5)', background: 'rgba(245, 166, 35, 0.1)' }}>
        <AlertTriangle size={13} style={{ display: 'inline', marginRight: 6, verticalAlign: '-2px' }} />
        {APPRISE_UNVERIFIED_NOTICE}
      </div>
      <div className={styles.approvalNotice} style={{ marginTop: 0, marginBottom: 12 }}>
        <ShieldCheck size={14} />
        <span>
          <strong>Confirming runs a Terraform plan first — nothing is created in Azure until a plan is approved.</strong>{' '}
          {approverSentence(options?.executor)}
        </span>
      </div>
      {options?.executor && applyIsDisabled(options.executor) && options.executor.note && (
        <div className={styles.lbInfoBanner} role="note" style={{ marginTop: 0, marginBottom: 12, borderColor: 'rgba(245, 166, 35, 0.5)', background: 'rgba(245, 166, 35, 0.1)' }}>
          {options.executor.note}
        </div>
      )}
      <div className={styles.lbInfoBanner} role="note" style={{ marginTop: 0, marginBottom: 16 }}>
        <strong>The Apprise application is NOT installed by this deployment.</strong> Only the Azure virtual machine and
        its supporting resources are provisioned and verified through the Azure control plane. The application track
        stays &ldquo;not configured&rdquo;.
      </div>
      <AppriseReviewGroups
        config={config} options={reviewOptions} region={region} subscription={subscription?.name}
        sizeSummary={size ? `${size.vcpus} vCPU, ${size.memory_gb} GiB` : undefined}
      />
    </div>
  )
}

interface CostProps {
  config: AppriseVmConfig
  region: string
  /** Estimated monthly cost of the other (non-Apprise) selected products. */
  otherProductsCost: number
}

export function AppriseCostSummary({ config, region, otherProductsCost }: CostProps) {
  const create = config.server.deployment_mode === 'create_new'
  const { size, catalog } = useSelectedSize(regionToLocation(region), config.server.vm_size, create)
  const price = create && size && size.price_month_usd != null && size.price_month_usd > 0 ? size.price_month_usd : null
  const fetching = create && (catalog.isLoading || (catalog.isFetching && !size))
  return (
    <div className={styles.costSummary}>
      <span className={styles.costLabel}>Estimated Total</span>
      <span className={styles.costValue}>
        {fetching ? 'Fetching prices…' : price == null ? 'Estimate unavailable' : `${formatCurrency(price + otherProductsCost)}/month`}
      </span>
      {!fetching && price != null && (
        <span style={{ display: 'block', fontSize: 11, color: 'var(--aptean-text-muted)', marginTop: 4 }}>
          VM compute only — disks, public IP, networking, backup and monitoring are not included.
          {size?.price_note ? ` ${size.price_note}` : ''}
        </span>
      )}
    </div>
  )
}
